import React, { useState, useEffect } from 'react';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@src/common/Button';
import { PageLayout } from '@src/common/PageLayout';
import { useFormFieldArray } from '@uhg-abyss/web/hooks/useFormFieldArray';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useSteps } from '@src/state/steps';
import { event as TaggingEvent } from '@uhg-abyss/web/tools/event';
import { useUser } from '@src/state/user/hooks';
import isEmpty from 'lodash/isEmpty';
import { getSessionRequestObj } from '@src/state/tinRegistration/utils';
import { useTinRegistration } from '@src/state/tinRegistration';
import { Loading } from '@src/common/Loading';
import { storage } from '@uhg-abyss/web/tools/storage';
import { useTinDrafts } from '@src/state/superUserDraft';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { useForm as useAbyssForm } from '@uhg-abyss/web/hooks/useForm';
import { TinHeader } from '../TinHeader';
import { PrimaryServicing } from './PrimaryServicing';
import { PrimaryBilling } from './PrimaryBilling';
import { PrimaryRemittance } from './PrimaryRemittance';
import { AddSecondaryServicing } from './AddSecondaryServicing';
import { SecondaryServicing } from './SecondaryServicing/SecondaryServicing';
import { adobeFields } from './adobeErrorFields';

const PageButtons = styled('div', {
  width: '11%',
  marginTop: '2em',
  paddingBottom: '25px',
  fontWeight: 600,
  fontSize: '16px',
  color: '$core.color.brand.100',
  fontFamily: '$heading',
  display: 'inline-flex',
  marginRight: '15px',
});

const PageDraftButton = styled('div', {
  width: 'fitContent',
  marginTop: '2em',
  paddingBottom: '25px',
  float: 'right',
  color: '$core.color.brand.100',
});

const PageDraftButtonFont = styled('div', {
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: '$heading',
});

const SaveDraftSuccessMessage = styled('div', {
  fontSize: '13px',
  color: '$success1',
  textAlign: 'center',
});

const SaveDraftFailedMessage = styled('div', {
  fontSize: '13px',
  textAlign: 'center',
  color: '$core.color.red.120',
});

const Space = styled('div', {
  marginTop: '30px',
});

export const Address = () => {
  const router = useRouter();
  const [addressCount, setAddressCount] = useState(0);
  const abyssForm = useAbyssForm();
  const addresses = abyssForm.getValues('address');

  const registration = abyssForm.getValues('searchRegistration');
  const userInfo = useUser.state();
  const isSuperUser = storage.local.get(userInfo.uuid);
  const { append, remove } = useFormFieldArray({
    control: abyssForm.control,
    name: 'address.secondaryServicing',
  });
  const practiceInfo = abyssForm.getValues('practiceInformation');
  const w9Form = abyssForm.getValues('w9form');
  const address = abyssForm.getValues('address');
  const signatureType = abyssForm.getValues('attestation.signatureRadio');
  const attestation = abyssForm.getValues('attestation');
  const { documentIds, trackingNumber } = useTinRegistration.state();
  const { saveAsDraft } = useTinRegistration.actions();
  const { saveSuperUserDraft } = useTinDrafts.actions();

  const {
    pagesVisited,
    pagesSubmitted,
    setStep,
    addPageVisited,
    addPageSubmitted,
    removeSubmittedPage,
  } = useSteps();
  const { tinDraft, isDraftDisabled } = useTinRegistration.state();
  const [isDataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    const pageName = 'epvsecure:tin registration: step3-locations';
    const billingTin = registration ? registration.billingTin : '';
    const siteSectionLevel = {
      siteSectionLevel1: 'tin registration',
      siteSectionLevel2: 'step3-locations',
      siteSectionLevel3: '',
      siteSectionLevel4: '',
      siteSectionLevel5: '',
    };
    TaggingEvent('ON_PAGE_LOAD', {
      userInfo,
      pageName,
      billingTin,
      siteSectionLevel,
    });
    if (pagesVisited) {
      if (!pagesVisited.includes('Locations')) {
        const pages = pagesVisited.concat('Locations');
        addPageVisited(pages);
      }
    } else {
      const pages = ['Locations'];
      addPageVisited(pages);
    }
    setStep(3);

    if (
      tinDraft &&
      tinDraft.w9Details &&
      tinDraft.practiceInformation &&
      tinDraft.address &&
      tinDraft.returnToRegistration &&
      !pagesSubmitted.includes('Locations')
    ) {
      setDataLoading(true);
      abyssForm.validate('address', (values) => {
        if (pagesSubmitted) {
          if (!pagesSubmitted.includes('Locations')) {
            const pages = pagesSubmitted.concat('Locations');
            addPageSubmitted(pages);
          }
        } else {
          const pages = ['Locations'];
          addPageSubmitted(pages);
        }
        router.navigate('/tin-registration/step/4');
      });
      setDataLoading(false);
    }
  }, []);

  const goBack = () => {
    abyssForm.validate(
      'address',
      (values) => {
        let emptyValue = false;
        Object.keys(values.primaryServicing).forEach((key) => {
          if (!values.primaryServicing[key] && key !== 'streetLine2') {
            emptyValue = true;
          }
        });
        Object.keys(values.primaryBilling).forEach((key) => {
          if (!values.primaryBilling[key] && key !== 'streetLine2') {
            emptyValue = true;
          }
        });
        Object.keys(values.primaryRemittance).forEach((key) => {
          if (!values.primaryRemittance[key] && key !== 'streetLine2') {
            emptyValue = true;
          }
        });
        Object.keys(values).forEach((key) => {
          if (!values[key] && key !== 'secondaryServicing') {
            emptyValue = true;
          }
          if (key === 'secondaryServicing') {
            Object.values(values.secondaryServicing).forEach((value) => {
              if (value) {
                Object.keys(value).forEach((keyValue) => {
                  if (!value[keyValue] && keyValue !== 'streetLine2') {
                    emptyValue = true;
                  }
                });
              }
            });
          }
        });
        if (emptyValue) {
          const updatedSubmittedList = pagesSubmitted.filter((item) => {
            return item !== 'Locations';
          });
          removeSubmittedPage(updatedSubmittedList);
        }
        TaggingEvent('LOCATIONS_BACK');
        router.navigate('/tin-registration/step/2');
      },
      (error) => {
        const fields = adobeFields(error);
        if (!isEmpty(fields)) {
          TaggingEvent('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };

  const continueRegistration = () => {
    abyssForm.validate(
      'address',
      (values) => {
        if (pagesSubmitted) {
          if (!pagesSubmitted.includes('Locations')) {
            const pages = pagesSubmitted.concat('Locations');
            addPageSubmitted(pages);
          }
        } else {
          const pages = ['Locations'];
          addPageSubmitted(pages);
        }
        TaggingEvent('LOCATIONS_CONTINUE');
        const eventData = {
          action: 'continue',
        };
        TaggingEvent('EAPM_TRANSACTION_CONTINUE', eventData);
        router.navigate('/tin-registration/step/4');
      },
      (error) => {
        const fields = adobeFields(error);
        if (!isEmpty(fields)) {
          TaggingEvent('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };

  const handleAddAddress = () => {
    append({
      streetLine1: '',
      streetLine2: '',
      city: '',
      state: '',
      zipCode: '',
    });
    setAddressCount(addressCount + 1);
  };

  const handleRemoveAddress = (index) => {
    setAddressCount(addressCount - 1);
    remove(index);
  };

  const loadSecondaryAddress = (index) => {
    return [...Array(index).keys()].map((address) => {
      return (
        <Grid.Col key={address} span="50%">
          <SecondaryServicing
            index={address}
            addressCount={addressCount}
            handleRemoveSecondaryAddress={() => {
              return handleRemoveAddress(address);
            }}
          />
        </Grid.Col>
      );
    });
  };

  const [saveDraftFailed, setSaveDraftFailed] = useState(false);
  const [saveDraftSuccess, setSaveDraftSuccess] = useState(false);

  const saveDraft = () => {
    const reqBody = getSessionRequestObj({
      w9Form,
      practiceInfo,
      address,
      attestation,
      documentIds,
      signatureType,
    });

    const interactionName = 'locations widget';
    const interactionValue = 'save as draft';
    const eventInteraction = 'interaction';
    TaggingEvent('SAVE_AS_DRAFT', {
      eventInteraction,
      interactionName,
      interactionValue,
    });

    const billingTin = registration ? registration.billingTin : '';
    const requestId = registration ? registration.requestId : '';
    const tNum = trackingNumber;
    setSaveDraftSuccess(false);
    setSaveDraftFailed(false);
    if (!isSuperUser) {
      saveAsDraft(
        {
          billingTin,
          requestId,
          trackingNumber: tNum,
          ...reqBody,
        },
        (response) => {
          setSaveDraftSuccess(true);
        },
        (error) => {
          setSaveDraftFailed(true);
        }
      );
    } else {
      saveSuperUserDraft(
        {
          billingTin,
          requestId,
          optumId: registration.optumId,
          trackingNumber: tNum,
          ...reqBody,
        },
        (response) => {
          setSaveDraftSuccess(true);
        },
        (error) => {
          setSaveDraftFailed(true);
        }
      );
    }
  };

  useEffect(() => {
    let count = 0;
    if (addresses && addresses.secondaryServicing) {
      addresses.secondaryServicing.forEach((value) => {
        if (value && value != null) {
          count += 1;
          setAddressCount(count);
        }
      });
    }
  }, []);

  return (
    <Loading isLoading={isDataLoading}>
      <PageLayout>
        <TinHeader />
        <Space />
        <Grid>
          <Grid.Col span="50%">
            <PrimaryServicing />
          </Grid.Col>
          <Grid.Col span="50%">
            <PrimaryBilling />
          </Grid.Col>
          <Grid.Col span="50%">
            <PrimaryRemittance />
          </Grid.Col>
          {addressCount === 1 && (
            <Grid.Col key={0} span="50%">
              <SecondaryServicing
                key={0}
                index={0}
                addressCount={addressCount}
                handleRemoveSecondaryAddress={() => {
                  return handleRemoveAddress(0);
                }}
              />
            </Grid.Col>
          )}
          {addressCount === 2 && loadSecondaryAddress(addressCount)}
          {addressCount === 3 && loadSecondaryAddress(addressCount)}
          {addressCount === 4 && loadSecondaryAddress(addressCount)}
          {addressCount === 5 && loadSecondaryAddress(addressCount)}
          <Grid.Col span="50%">
            <AddSecondaryServicing
              addressCount={addressCount}
              handleAddSecondaryAddress={handleAddAddress}
            />
          </Grid.Col>
        </Grid>
        <PageButtons>
          <Layout.Group>
            <Button
              type="button"
              id="locations-back-button"
              aria-label="click here to go back"
              data-testid="start-button"
              onClick={goBack}
              width="100%"
              variant="outline"
            >
              Back
            </Button>
            <Button
              type="button"
              id="locations-continue-button"
              aria-label="click here to continue"
              data-testid="start-button"
              onClick={continueRegistration}
              width="100%"
            >
              Continue
            </Button>
          </Layout.Group>
        </PageButtons>
        <PageDraftButton>
          <PageDraftButtonFont>
            <Button
              type="button"
              id="draft-continue-button"
              aria-label="click here to save as draft"
              data-testid="draft-button"
              onClick={saveDraft}
              width="100%"
              variant="outline"
              isDisabled={isDraftDisabled}
            >
              Save as draft
            </Button>
          </PageDraftButtonFont>
          {saveDraftSuccess && (
            <SaveDraftSuccessMessage id="savedraft-success">
              Saved successfully.
            </SaveDraftSuccessMessage>
          )}
          {saveDraftFailed && (
            <SaveDraftFailedMessage id="savedraft-failed">
              Failed to save.
            </SaveDraftFailedMessage>
          )}
        </PageDraftButton>
      </PageLayout>
    </Loading>
  );
};
